import axios, { AxiosError, AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { GetOIDCToken } from "src/actions/OIDC";
import Config from "../config";
import { Provider } from "../types";
import { ErrorResponse, ErrorType } from "./errorReponse";
import { evaluateProviderMigration } from "./be";

type ProviderResponse = {
  id: string;
  name: string;
  logo: string;
  logo_white: string;
  url: string;
  is_default_authentication: boolean;
  client_id_for_alf: boolean;
  is_new_integration: boolean;
};

type ProviderResponseV2 = ProviderResponse & {
  slug: string;
  has_package_revalidation: boolean;
  client_id_for_alf: boolean;
  sales_id: string;
  is_hidden?: boolean;
};

type ProvidersResponse = {
  providers: Array<ProviderResponse>;
};

type IntegratorCallbackRequest = {
  providerId: string;
  code: string;
  state: string;
  providerCallbackUrl?: string | undefined;
  user?: string;
  password?: string;
  params?: object;
};

export type IntegratorCallbackResponse = {
  userId: string;
  providerId: string;
  state: string;
  isNewIntegration: boolean;
};

export type IntegratorCallbackResponseError = {
  code?: number;
  errorId?: string;
  providerName?: string;
  providerLogo?: string;
};

export type IntegratorGetRedirectUrlResponse = {
  redirectUrl: string;
};

export const PROVIDER_ERROR_CODE = 502;

export const isGSatCallback = (path: string) => /gsat/g.test(path);

export const useIntegratorCallback = (
  code: string,
  state: string,
  params: object
) => {
  const [response, setResponse] = useState<IntegratorCallbackResponse>({
    userId: "",
    providerId: "",
    state: "",
    isNewIntegration: false
  });

  const [errorResponse, setErrorResponse] = useState<
    IntegratorCallbackResponseError
  >();

  useEffect(() => {
    integratorCallback(code, state, undefined, undefined, params)
      .then((response: AxiosResponse<IntegratorCallbackResponse>) => {
        setResponse(response.data);
      })
      .catch((error: AxiosError<IntegratorCallbackResponseError>) => {
        if (
          error.response?.data.code ||
          error.response?.status === PROVIDER_ERROR_CODE
        ) {
          setErrorResponse(error.response.data);
        } else {
          const errorId = error.response?.data.errorId ?? "";
          setErrorResponse({ code: 100, errorId });
        }
      });
  }, [code, state]);

  return { response, errorResponse };
};

export const integratorCallback = async (
  code: string,
  state: string,
  user?: string,
  password?: string,
  params?: object
) => {
  const postUrl = isGSatCallback(window.location.pathname)
    ? "/oauth/callback/gsat"
    : "/oauth/callback";

  const providerCallbackUrl =
    localStorage.getItem("providerCallbackUrl") || undefined;

  const requestData: IntegratorCallbackRequest = {
    providerId: localStorage.getItem("providerId") || "",
    code: code || "",
    state: state,
    providerCallbackUrl: providerCallbackUrl,
    user: user,
    password: password,
    params: params
  };

  let url = Config.INT_BASE_URL;
  let isMigrated = evaluateProviderMigration(requestData.providerId);

  if (await isMigrated) {
    url = Config.BE_BASE_URL + "/v1";
  }

  return axios.post(`${url}${postUrl}`, requestData, {
    headers: {
      Authorization: `Bearer ${GetOIDCToken()}`
    }
  });
};

export const useIntegratorGetRedirectUrl = (
  code: string,
  state: string,
  providerId: string
) => {
  const [response, setResponse] = useState<IntegratorGetRedirectUrlResponse>({
    redirectUrl: ""
  });

  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    integratorGetRedirectUrl(providerId, code, state)
      .then((response: AxiosResponse<IntegratorGetRedirectUrlResponse>) => {
        setResponse(response.data);
      })
      .catch((error: AxiosError) => {
        setError(true);
      });
  }, [code, state]);

  return { response, error };
};

export const integratorGetRedirectUrl = (
  providerId: string,
  code: string,
  state: string
) => {
  return axios.get(
    `${Config.INT_BASE_URL}/oauth/${providerId}/redirect?code=${code}&state=${state}`,
    {
      headers: {
        Authorization: `Bearer ${GetOIDCToken()}`
      }
    }
  );
};

export const getProviderByProviderID = async (
  providerID: string,
  retries = 2
): Promise<Provider> => {
  return await axios
    .get(`${Config.INT_BASE_URL}/v1/providers/${providerID}`)
    .then((response: AxiosResponse<ProviderResponseV2>) => {
      return {
        id: response.data.id,
        name: response.data.name,
        logo: response.data.logo,
        logo_white: response.data.logo_white,
        url: response.data.url,
        is_default_authentication: response.data.is_default_authentication,
        client_id_for_alf: response.data.client_id_for_alf,
        is_new_integration: response.data.is_new_integration,
        is_hidden: response.data.is_hidden,
        slug: response.data.slug,
        has_package_revalidation: response.data.has_package_revalidation,
        sales_id: response.data.sales_id
      } as Provider;
    })
    .catch((error: AxiosError<ErrorResponse>) => {
      if (retries > 0) {
        return getProviderByProviderID(providerID, retries - 1);
      }

      throw new ErrorResponse(
        "Error to get redirect URL",
        ErrorType.ProviderGenericError,
        error.response?.data.errorId || ""
      );
    });
};
